import type {WizardData} from '../index'
import type {AllocationScope} from '../types/teacherAllocationAi'
import {lookupSubjectId} from './levelCurriculum'

export const parsePositiveNumber = (value: unknown): number | null => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

export const getTeacherFullName = (t: any): string =>
  (t?._nameInput !== undefined
    ? String(t._nameInput || '').trim()
    : `${t?.first_name || ''} ${t?.last_name || ''}`.trim()
  ).trim()

export const buildAllocationSlotKey = (classKey: string, subjectName: string): string =>
  `${classKey}|||${subjectName}`

export const parseAllocationSlotKey = (
  slotKey: string
): {classKey: string; subjectName: string} => {
  const idx = slotKey.lastIndexOf('|||')
  if (idx === -1) return {classKey: slotKey, subjectName: ''}
  return {classKey: slotKey.slice(0, idx), subjectName: slotKey.slice(idx + 3)}
}

export const getTeacherEmployeeRef = (teacher: any): string => {
  const empId = String(teacher?.employeeId ?? '').trim()
  if (empId) return empId
  return getTeacherFullName(teacher)
}

export const findTeacherByRef = (
  teachers: any[],
  employeeId?: string | null,
  name?: string | null
) => {
  const emp = String(employeeId ?? '').trim()
  if (emp) {
    const byEmp = teachers.find(
      (t) => String(t?.employeeId ?? '').trim() === emp || getTeacherEmployeeRef(t) === emp
    )
    if (byEmp) return byEmp
  }
  const teacherName = String(name ?? '').trim()
  if (teacherName) {
    return teachers.find((t) => getTeacherFullName(t) === teacherName)
  }
  return undefined
}

export const getTeacherDisplayNameFromApi = (t: any): string => {
  const first = String(t?.first_name ?? '').trim()
  const last = String(t?.last_name ?? '').trim()
  if (first && last && /^\d+$/.test(last)) {
    return first
  }
  return [first, last].filter(Boolean).join(' ').trim()
}

export type AllocationIdCheckParams = {
  manualData: WizardData['manualData']
  scope: AllocationScope
  activeFilters: {branch?: string; level?: string; curriculum?: string}
  normalizeBranchName: (branch?: string) => string
  getSubjectsForClass: (classItem: any) => string[]
  getLessonClassKey: (className: string, branch?: string, curriculum?: string) => string
  isSlotDeleted: (classKey: string, subjectName: string) => boolean
}

export function getClassesInScope(
  classes: any[],
  scope: AllocationScope,
  activeFilters: AllocationIdCheckParams['activeFilters'],
  normalizeBranchName: (branch?: string) => string
): any[] {
  if (scope === 'all_branches') return classes
  const branch = normalizeBranchName(activeFilters.branch)
  const level = (activeFilters.level || '').trim()
  const curriculum = (activeFilters.curriculum || '').trim()

  return classes.filter((c: any) => {
    if (normalizeBranchName(c?.branch) !== branch) return false
    if (level && (c?.level || '').toString().trim() !== level) return false
    if (curriculum && String(c?.curriculum || '').trim() !== curriculum) return false
    return true
  })
}

export function checkAllocationIdsReady(params: AllocationIdCheckParams): {
  ready: boolean
  missingSections: Array<'teachers' | 'subjects'>
  missingSubjectSlots: number
} {
  const {
    manualData,
    scope,
    activeFilters,
    normalizeBranchName,
    getSubjectsForClass,
    getLessonClassKey,
    isSlotDeleted,
  } = params

  const teachers = manualData.teachers ?? []
  const classes = manualData.classes ?? []
  const subjectIdByNameAndClass =
    ((manualData as any).subjectIdByNameAndClass as Record<string, number>) ?? {}

  const teachersMissingId = teachers.some((t: any) => !parsePositiveNumber((t as any)?.id))
  const scopedClasses = getClassesInScope(classes, scope, activeFilters, normalizeBranchName)

  let missingSubjectSlots = 0
  scopedClasses.forEach((cls: any) => {
    const classId = parsePositiveNumber((cls as any)?.id)
    if (!classId) return
    const className = String(cls?.name ?? '').trim()
    const classKey = getLessonClassKey(className, cls?.branch, cls?.curriculum)
    getSubjectsForClass(cls).forEach((subjectName) => {
      if (!subjectName || isSlotDeleted(classKey, subjectName)) return
      const subjectId = lookupSubjectId(
        subjectIdByNameAndClass,
        subjectName,
        className,
        cls?.curriculum,
        cls?.branch
      )
      if (!subjectId) missingSubjectSlots += 1
    })
  })

  const classesMissingId = scopedClasses.some((c: any) => !parsePositiveNumber((c as any)?.id))

  const missingSections: Array<'teachers' | 'subjects'> = []
  if (teachersMissingId) missingSections.push('teachers')
  if (classesMissingId || missingSubjectSlots > 0) missingSections.push('subjects')

  return {
    ready: missingSections.length === 0,
    missingSections: Array.from(new Set(missingSections)),
    missingSubjectSlots,
  }
}
